import React, { useState, useEffect } from 'react';
import { Timeline, Tag, Spin, Empty, Collapse, Typography } from 'antd';
import { HistoryOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getEntityHistory } from '../api';

const { Text } = Typography;

const ACTION_COLORS: Record<string, string> = {
  CREATE: 'green',
  DELETE: 'red',
  UPDATE: 'blue',
  APPROVE: 'green',
  REJECT: 'red',
  STATUS_CHANGE: 'orange',
  SUBMIT: 'cyan',
  RECEIVE: 'purple',
  RETURN: 'magenta',
};

interface EntityHistoryProps {
  entityType: string;
  entityId: string;
}

const EntityHistory: React.FC<EntityHistoryProps> = ({ entityType, entityId }) => {
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const fetchHistory = async () => {
    if (loaded) return;
    setLoading(true);
    try {
      const data = await getEntityHistory(entityType, entityId);
      setHistory(data);
      setLoaded(true);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  return (
    <Collapse
      ghost
      onChange={(keys) => {
        if (keys.length > 0) fetchHistory();
      }}
      items={[
        {
          key: 'history',
          label: (
            <span>
              <HistoryOutlined style={{ marginRight: 8 }} />
              Audit Log {loaded ? `(${history.length})` : ''}
            </span>
          ),
          children: loading ? (
            <Spin size="small" />
          ) : history.length === 0 ? (
            <Empty description="No changes recorded" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <Timeline
              items={history.map((entry: any) => ({
                color: ACTION_COLORS[entry.action] || 'gray',
                children: (
                  <div key={entry.id}>
                    <div>
                      <Text strong>{dayjs(entry.timestamp).format('YYYY-MM-DD HH:mm:ss')}</Text>
                      {' - '}
                      <Text>{entry.user?.displayName || 'System'}</Text>
                      {' '}
                      <Tag color={ACTION_COLORS[entry.action] || 'default'} style={{ marginLeft: 4 }}>
                        {entry.action || 'CHANGE'}
                      </Tag>
                    </div>
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary">{entry.fieldName}</Text>
                      {entry.oldValue || entry.newValue ? (
                        <>
                          {': '}
                          {entry.oldValue && (
                            <Text delete type="danger" style={{ marginRight: 4 }}>
                              {entry.oldValue}
                            </Text>
                          )}
                          {entry.oldValue && entry.newValue && ' \u2192 '}
                          {entry.newValue && (
                            <Text type="success">{entry.newValue}</Text>
                          )}
                        </>
                      ) : null}
                    </div>
                  </div>
                ),
              }))}
            />
          ),
        },
      ]}
    />
  );
};

export default EntityHistory;
